import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Check, Plus, Sparkles, Eye, ShieldCheck, X } from 'lucide-react';
import ScoreRing from '../components/ScoreRing';
import ModuleExplainer from '../components/ModuleExplainer';
import BrandPrimer from '../components/BrandPrimer';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { User as A1, FileText as A2, Linkedin as A3, MessageSquare as A4, Search as A5, Sparkles as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/career-growth`;

const RAISES_HUE = {
  Visibility: '#a78bfa',
  'Professional Presence': '#f472b6',
  'Digital Professionalism': '#22d3ee',
  Experience: '#34d399',
  Network: '#fb923c',
};

export default function PositionMePage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headline, setHeadline] = useState('');
  const [about, setAbout] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [pillars, setPillars] = useState([]);
  const [newPillar, setNewPillar] = useState('');

  const apply = (data) => {
    setD(data);
    setHeadline(data.headline || '');
    setAbout(data.about || '');
    setLinkedin(data.linkedin_url || '');
    setPillars(data.pillars || []);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/position`, getAuthHeader());
      apply(res.data);
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/position`, {
        headline, about, pillars, linkedin_url: linkedin, ...extra,
      }, getAuthHeader());
      apply(res.data);
      celebrate('Your brand is on the record. That is how people will find you.');
    } catch { toast.error('Could not save. Try again.'); }
    finally { setSaving(false); }
  };

  const toggleCheck = (key) => {
    const done = new Set((d.professionalism_checks || []).filter(c => c.done).map(c => c.key));
    done.has(key) ? done.delete(key) : done.add(key);
    save({ professionalism: [...done] });
  };

  const addPillar = (p) => {
    const v = (p || '').trim();
    if (!v || pillars.includes(v) || pillars.length >= 3) {
      if (pillars.length >= 3) toast.error('Three pillars is the point, swap one out first.');
      return;
    }
    setPillars([...pillars, v]);
    setNewPillar('');
  };

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="position-arrival"
        eyebrow="My Brand"
        title="Decide what you are known for."
        subtitle="Before anyone reads your résumé they form an impression in about six seconds. This is where you choose what that impression is, your headline, your story, your profile and the way you introduce yourself."
        accent="#a78bfa"
        second="#f472b6"
        ctaLabel="Build my brand"
        narration={"This is My Brand. Every hiring decision starts with an impression formed in seconds, from a headline, a profile or a first sentence. Rather than leave that to chance, you will write it deliberately: what you want to be known for, the story that explains your path, a profile that reads as intentional, and an introduction you can say out loud without cringing. We draft with you, in your own words, never a generic template."}
        facts={[
          { icon: A6, head: "Written with you", sub: "Your words, sharpened, not a template" },
          { icon: A5, head: "Search yourself", sub: "See what a recruiter sees before they meet you" },
          { icon: A4, head: "Say it out loud", sub: "An introduction that sounds like a person" }
        ]}
        modules={[
          { icon: A1, label: "Your headline", line: "One line that says what you do and who for", hue: "#a78bfa" },
          { icon: A2, label: "Your story", line: "Why this path, told so a career change reads as intent", hue: "#22d3ee" },
          { icon: A3, label: "Profile and presence", line: "Profile, summary and the details people skim", hue: "#34d399" },
          { icon: A4, label: "Introductions", line: "What to say in a room, a message and an interview", hue: "#fbbf24" },
          { icon: A5, label: "Search audit", line: "What comes up when someone looks you up", hue: "#f472b6" },
          { icon: A6, label: "Portfolio", line: "Proof of the work, arranged so it is easy to believe", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Position Me, My Brand & Visibility"
        description="How you look to someone who doesn't know you yet, on LinkedIn and anywhere else they check."
      />

      <GlassCard className="mb-6" data-testid="position-scores">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <ScoreRing value={d.visibility_score || 0} label="Visibility" palette="violet" testId="visibility-score" />
          <ScoreRing value={d.professionalism_score || 0} label="Professionalism" palette="cyan" size={170} testId="professionalism-score" />
          <div className="flex-1 w-full">
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Positioning toward</p>
            <p className="text-lg font-semibold text-white">{d.target || 'No target career set yet'}</p>
            <div className="mt-4 space-y-2 max-w-md">
              {(d.visibility_breakdown || []).map(b => (
                <div key={b.label} className="flex items-center gap-3">
                  <span className="w-56 shrink-0 text-xs text-slate-300">{b.label}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-700/70 overflow-hidden">
                    <div className="h-full rounded-full"
                      style={{
                        width: `${Math.min((b.points / 20) * 100, 100)}%`,
                        background: 'linear-gradient(90deg,#a78bfa,#f472b6)',
                        boxShadow: b.points ? '0 0 10px rgba(167,139,250,.6)' : 'none',
                        transition: 'width 1s cubic-bezier(.22,1,.36,1)',
                      }} />
                  </div>
                  <span className={`w-8 text-right text-xs tabular-nums ${b.points ? 'text-violet-300' : 'text-slate-300'}`}>+{b.points}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </GlassCard>

      <BrandPrimer />

      <ModuleExplainer id="position" />

      {/* Brand pillars */}
      <GlassCard className="mb-6" data-testid="pillars-card">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-cyan-300" /> Brand pillars
        </h3>
        <p className="text-xs text-slate-300 mb-4">Three things your name should stand for. Pulled from what you've already told the platform.</p>

        <div className="flex flex-wrap gap-2 mb-4 min-h-[36px]">
          {pillars.map(p => (
            <span key={p} data-testid={`pillar-${p}`}
              className="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs text-white border border-violet-400/50"
              style={{ background: 'linear-gradient(90deg, rgba(167,139,250,.28), rgba(244,114,182,.24))', boxShadow: '0 0 18px rgba(167,139,250,.3)' }}>
              {p}
              <button onClick={() => setPillars(pillars.filter(x => x !== p))} className="opacity-60 hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {pillars.length === 0 && <span className="text-xs text-slate-300">None picked yet, tap a suggestion below.</span>}
        </div>

        <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-2">Suggested for you</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {(d.suggested_pillars || []).filter(s => !pillars.includes(s)).map(s => (
            <button key={s} onClick={() => addPillar(s)} data-testid={`suggested-pillar-${s}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-slate-800 border border-violet-500/30 text-violet-200 hover:border-violet-400/70 hover:text-white hover:bg-violet-500/10 transition-colors duration-200">
              <Plus className="w-3 h-3" />{s}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input value={newPillar} onChange={e => setNewPillar(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addPillar(newPillar)}
            placeholder="Or write your own pillar" data-testid="input-new-pillar" />
          <Button variant="outline" onClick={() => addPillar(newPillar)} data-testid="add-pillar-btn">Add</Button>
        </div>
      </GlassCard>

      {/* Headline + About + LinkedIn */}
      <GlassCard className="mb-6" data-testid="brand-copy-card">
        <h3 className="text-base md:text-lg font-semibold text-white mb-4">Headline, About and LinkedIn</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">Headline, name the work you want</label>
            <Input value={headline} onChange={e => setHeadline(e.target.value)}
              placeholder="Operations leader moving into cybersecurity | Risk · Compliance · Security Analysis"
              data-testid="input-headline" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">About, the story of where you're going</label>
            <textarea value={about} onChange={e => setAbout(e.target.value)} rows={5} data-testid="input-about"
              placeholder="Fifteen years running bank operations taught me how risk actually shows up in a business…"
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors duration-200" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">LinkedIn URL</label>
            <Input value={linkedin} onChange={e => setLinkedin(e.target.value)}
              placeholder="linkedin.com/in/yourname" data-testid="input-linkedin" />
          </div>
        </div>
        <Button className="mt-4" onClick={() => save()} disabled={saving} data-testid="save-position-btn">
          {saving ? 'Saving…' : 'Save my positioning'}
        </Button>
      </GlassCard>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Professionalism */}
        <GlassCard data-testid="professionalism-card">
          <h3 className="text-base md:text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-cyan-300" /> Digital professionalism
          </h3>
          <p className="text-xs text-slate-300 mb-4">Habits, not a one-time task. Tap what's true today.</p>
          <div className="space-y-2">
            {(d.professionalism_checks || []).map(c => (
              <button key={c.key} onClick={() => toggleCheck(c.key)} data-testid={`check-${c.key}`}
                className={`w-full flex items-start gap-3 text-left p-3 rounded-lg border transition-colors duration-200 ${
                  c.done ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`}>
                <span className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 ${
                  c.done ? 'bg-emerald-400 text-slate-900' : 'border border-slate-500'}`}>
                  {c.done && <Check className="w-3 h-3" />}
                </span>
                <span className={`text-sm ${c.done ? 'text-emerald-100' : 'text-slate-300'}`}>{c.label}</span>
              </button>
            ))}
          </div>
        </GlassCard>

        {/* Continuous plan */}
        <GlassCard data-testid="position-plan-card">
          <h3 className="text-base md:text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <Eye className="w-4 h-4 text-violet-300" /> Your positioning work
          </h3>
          <p className="text-xs text-slate-300 mb-4">{d.plan_note}</p>
          <ol className="space-y-2">
            {(d.plan || []).map((s, i) => {
              const hue = RAISES_HUE[s.raises] || '#22d3ee';
              return (
                <li key={i} className="flex gap-3 p-3 rounded-lg border border-slate-700 bg-slate-800/40"
                  style={{ borderLeft: `3px solid ${hue}`, boxShadow: `inset 0 0 24px ${hue}14` }}>
                  <span className="w-5 h-5 rounded-full text-xs flex items-center justify-center shrink-0"
                    style={{ background: `${hue}2e`, color: hue }}>{i + 1}</span>
                  <div>
                    <p className="text-sm text-slate-200">{s.step}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: hue }}>{s.effort} · raises {s.raises}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </GlassCard>
      </div>
    </AppLayout>
  );
}
